import React, { useState, useEffect, useRef } from "react";
import { useWebSocket } from "./WebSocketContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Button } from "@mui/material";

// TODO: Test this page for failure, http://localhost:3000/callback?code=<invalid code>&state=2
const CallbackOutlookApiPage = () => {
    const { t } = useTranslation();
    const { ws, subscribeToMessages } = useWebSocket();
    const navigate = useNavigate();
    const [caseId, setCaseId] = useState("") // Replace with the actual case ID
    const [exportId, setExportId] = useState(null);
    const [datasourceId, setDatasourceId] = useState(null);
    const [connectorType, setConnectorType] = useState('');
    const [oauthProvider, setOauthProvider] = useState('');
    const [flowType, setFlowType] = useState('datasource'); // datasource | action | pluginAction | pluginConnector | gmail | gmailAction | teamsAction | linkedinAction | onedrive | googledrive
    const [rawState, setRawState] = useState(null);
    const [code, setCode] = useState(null);
    const [result, setResult] = useState(null);
    const caseIdRef = useRef('');
    const exportIdRef = useRef(null);
    const datasourceIdRef = useRef(null);
    const connectorTypeRef = useRef('');
    const oauthProviderRef = useRef('');
    const flowTypeRef = useRef('datasource');
    const returnUrlRef = useRef(null);
    const exchangeSentRef = useRef(false);
    const exchangeCodeRef = useRef(null);

    useEffect(() => {
        console.log("CallbackOutlookApi mounted", { href: window.location.href });
        return () => {
            console.log("CallbackOutlookApi unmounted", { href: window.location.href });
        };
    }, []);

    useEffect(() => {
        caseIdRef.current = caseId;
    }, [caseId]);

    useEffect(() => {
        exportIdRef.current = exportId;
    }, [exportId]);

    useEffect(() => {
        datasourceIdRef.current = datasourceId;
    }, [datasourceId]);

    useEffect(() => {
        connectorTypeRef.current = connectorType;
    }, [connectorType]);

    useEffect(() => {
        oauthProviderRef.current = oauthProvider;
    }, [oauthProvider]);

    useEffect(() => {
        flowTypeRef.current = flowType;
    }, [flowType]);

    // Extract parameters from the URL on page load
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const codeFromUrl = params.get("code");
        const stateFromUrl = params.get("state");

        setCode(codeFromUrl);
        setRawState(stateFromUrl);

        const decodedPayload = stateFromUrl ? decodeOutlookState(stateFromUrl) : null;

        if (decodedPayload?.kind === "plugin_action_oauth") {
            if (decodedPayload?.caseId) {
                setCaseId(decodedPayload.caseId);
            }
            if (decodedPayload?.exportId) {
                setExportId(decodedPayload.exportId);
            }
            if (decodedPayload?.provider) {
                setOauthProvider(String(decodedPayload.provider));
            }
            if (decodedPayload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(decodedPayload.returnUrl);
            }
            setFlowType('pluginAction');
            console.log("Flow type set to: pluginAction, exportId:", decodedPayload?.exportId);
            return;
        }

        if (decodedPayload?.kind === "plugin_connector_oauth") {
            if (decodedPayload?.caseId) {
                setCaseId(decodedPayload.caseId);
            }
            if (decodedPayload?.datasourceId) {
                setDatasourceId(decodedPayload.datasourceId);
            }
            if (decodedPayload?.connectorType) {
                setConnectorType(String(decodedPayload.connectorType));
            }
            if (decodedPayload?.provider) {
                setOauthProvider(String(decodedPayload.provider));
            }
            if (decodedPayload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(decodedPayload.returnUrl);
            }
            setFlowType('pluginConnector');
            console.log("Flow type set to: pluginConnector, datasourceId:", decodedPayload?.datasourceId);
            return;
        }

        if (stateFromUrl && stateFromUrl.startsWith("oa.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.exportId) {
                setExportId(payload.exportId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('action');
            console.log("Flow type set to: action, exportId:", payload?.exportId);
        } else if (stateFromUrl && stateFromUrl.startsWith("od.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('datasource');
            console.log("Flow type set to: datasource");
        } else if (stateFromUrl && stateFromUrl.startsWith("odv.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('onedrive');
            console.log("Flow type set to: onedrive");
        } else if (stateFromUrl && stateFromUrl.startsWith("gsa.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.exportId) {
                setExportId(payload.exportId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('gmailAction');
            console.log("Flow type set to: gmailAction, exportId:", payload?.exportId);
        } else if (stateFromUrl && stateFromUrl.startsWith("tsa.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.exportId) {
                setExportId(payload.exportId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('teamsAction');
            console.log("Flow type set to: teamsAction, exportId:", payload?.exportId);
        } else if (stateFromUrl && stateFromUrl.startsWith("lsa.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.exportId) {
                setExportId(payload.exportId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('linkedinAction');
            console.log("Flow type set to: linkedinAction, exportId:", payload?.exportId);
        } else if (stateFromUrl && stateFromUrl.startsWith("ga.")) {
            // Gmail authorization flow: state = "ga.<encoded payload>" (legacy: "ga.{caseId}")
            const payload = decodeOutlookState(stateFromUrl);
            const casePart = payload?.caseId || stateFromUrl.substring(3);
            setCaseId(casePart);
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('gmail');
            setRawState(stateFromUrl);
            console.log("Flow type set to: gmail, caseId:", casePart);
        } else if (stateFromUrl && stateFromUrl.startsWith("gd.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('googledrive');
            console.log("Flow type set to: googledrive");
        } else {
            setCaseId(stateFromUrl);
            setFlowType('datasource');
            console.log("Flow type set to: datasource");
        }
    }, []); // Only run once on page load

    // Trigger exchangeAccessToken only when both code and caseId are available
    useEffect(() => {
        if (!code || !rawState || !ws) {
            return;
        }
        try {
            console.log("OAuth exchange effect fired", {
                code,
                state: rawState,
                flowType,
                href: window.location.href,
                hasOpener: !!window.opener
            });
        } catch (err) {
            // ignore logging errors
        }
        if (exchangeSentRef.current) {
            console.log("OAuth exchange skipped: already sent for this page load");
            return;
        }
        if (isExchangeCodeUsed(code)) {
            console.log("OAuth exchange skipped: code marked used", { code });
            handleDuplicateExchangeRedirect(navigate, flowTypeRef, returnUrlRef, caseIdRef, exportIdRef, datasourceIdRef, connectorTypeRef);
            return;
        }
        markExchangeCodeUsed(code);
        exchangeCodeRef.current = code;

        if (flowType === 'action') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                console.log("Sending exchangeOutlookActionToken", { caseId, exportId });
                ws.send(JSON.stringify({
                    command: "exchangeOutlookActionToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId
                }));
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'pluginAction') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                console.log("Sending exchangePluginActionOAuthToken", { caseId, exportId, provider: oauthProvider });
                ws.send(JSON.stringify({
                    command: "exchangePluginActionOAuthToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId,
                    ...(oauthProvider ? { provider: oauthProvider } : {}),
                }));
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'pluginConnector') {
            if (caseId && datasourceId && connectorType) {
                exchangeSentRef.current = true;
                console.log("Sending exchangePluginConnectorOAuthToken", { caseId, datasourceId, connectorType, provider: oauthProvider });
                ws.send(JSON.stringify({
                    command: "exchangePluginConnectorOAuthToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    datasourceId,
                    type: connectorType,
                    ...(oauthProvider ? { provider: oauthProvider } : {}),
                }));
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'gmailAction') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                console.log("Sending exchangeGmailActionToken", { caseId, exportId });
                ws.send(JSON.stringify({
                    command: "exchangeGmailActionToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId
                }));
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'gmail') {
            if (caseId) {
                exchangeSentRef.current = true;
                console.log("Sending exchangeGmailToken", { caseId });
                ws.send(JSON.stringify({ command: "exchangeGmailToken", code, caseId }));
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'onedrive') {
            if (caseId) {
                exchangeSentRef.current = true;
                console.log("Sending exchangeOneDriveToken", { caseId });
                ws.send(JSON.stringify({ command: "exchangeOneDriveToken", code, caseId }));
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'googledrive') {
            if (caseId) {
                exchangeSentRef.current = true;
                console.log("Sending exchangeGoogleDriveToken", { caseId });
                ws.send(JSON.stringify({ command: "exchangeGoogleDriveToken", code, caseId }));
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'teamsAction') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                console.log("Sending exchangeTeamsActionToken", { caseId, exportId });
                ws.send(JSON.stringify({
                    command: "exchangeTeamsActionToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId
                }));
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'linkedinAction') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                console.log("Sending exchangeLinkedinActionToken", { caseId, exportId });
                ws.send(JSON.stringify({
                    command: "exchangeLinkedinActionToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId
                }));
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (caseId) {
            exchangeSentRef.current = true;
            console.log("Sending exchangeOutlookToken", { caseId });
            ws.send(JSON.stringify({ command: "exchangeOutlookToken", code, caseId }));
            setResult(true);
        } else {
            console.log("Code or caseId not found in URL, skipping token exchange...");
            setResult(false);
        }
    }, [ws, code, caseId, exportId, datasourceId, connectorType, oauthProvider, rawState, flowType]); // Re-run when dependencies change
    useEffect(() => {
        const unsubscribe = subscribeToMessages((parsedMessage) => {
            // Message is already parsed from WebSocketContext
            const handleDatasourceTokenResult = (provider, payload) => {
                const res = payload.result ?? payload.success ?? parsedMessage.result;
                const { error } = payload;
                setResult(res);
                const targetCaseId = caseIdRef.current || payload.caseId || '';
                const redirectPath = targetCaseId ? `/case/${targetCaseId}` : '/case';

                if (!res && error) {
                    console.error(`Error exchanging ${provider} token:`, error);
                }

                const returnUrl = returnUrlRef.current;
                if (res && returnUrl) {
                    if (flowTypeRef.current === 'datasource') {
                        navigate(ensureDatasourceModal(returnUrl, "OutlookAPI"));
                    } else if (flowTypeRef.current === 'gmail') {
                        navigate(ensureDatasourceModal(returnUrl, "Gmail"));
                    } else if (flowTypeRef.current === 'onedrive') {
                        navigate(ensureDatasourceModal(returnUrl, "OneDrive"));
                    } else if (flowTypeRef.current === 'googledrive') {
                        navigate(ensureDatasourceModal(returnUrl, "GoogleDrive"));
                    } else {
                        navigate(returnUrl);
                    }
                    return;
                }

                if (provider === 'outlook') {
                    if (flowTypeRef.current === 'datasource') {
                        navigate(`${redirectPath}?openDatasource=true&connector=OutlookAPI`);
                    } else {
                        navigate(redirectPath);
                    }
                } else if (provider === 'gmail') {
                    navigate(`${redirectPath}?openDatasource=true&connector=Gmail`);
                } else if (provider === 'onedrive') {
                    navigate(`${redirectPath}?openDatasource=true&connector=OneDrive`);
                } else if (provider === 'googledrive') {
                    navigate(`${redirectPath}?openDatasource=true&connector=GoogleDrive`);
                }
            };

            if (parsedMessage.command === "tokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const providerType = (payload.type || payload.provider || '').toString().toLowerCase();
                if (providerType === 'outlookapi') {
                    console.log("Received token result (Outlook):", parsedMessage);
                    handleDatasourceTokenResult('outlook', payload);
                } else if (providerType === 'gmail') {
                    console.log("Received token result (Gmail):", parsedMessage);
                    handleDatasourceTokenResult('gmail', payload);
                } else if (providerType === 'onedrive') {
                    console.log("Received token result (OneDrive):", parsedMessage);
                    handleDatasourceTokenResult('onedrive', payload);
                } else if (providerType === 'googledrive') {
                    console.log("Received token result (Google Drive):", parsedMessage);
                    handleDatasourceTokenResult('googledrive', payload);
                }
                return;
            }

            if (parsedMessage.command === "pluginActionOAuthTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging plugin action token:", error);
                }
                const targetCaseId = caseIdRef.current || payload.caseId || '';
                const targetExportId = exportIdRef.current || payload.exportId;
                const redirectPath = targetCaseId ? `/case/${targetCaseId}` : '/case';
                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    navigate(returnUrl);
                    return;
                }
                if ((flowTypeRef.current === 'pluginAction' || flowTypeRef.current === 'action') && targetExportId) {
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                    return;
                }
                navigate(redirectPath);
                return;
            }

            if (parsedMessage.command === "pluginConnectorOAuthTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging plugin connector token:", error);
                }
                const targetCaseId = caseIdRef.current || payload.caseId || '';
                const targetConnectorType = connectorTypeRef.current || payload.type || payload.connectorType || '';
                const redirectPath = targetCaseId ? `/case/${targetCaseId}` : '/case';
                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    const sanitized = ensureDatasourceModal(returnUrl, targetConnectorType || "OutlookAPI");
                    navigate(sanitized);
                    return;
                }
                if (targetConnectorType) {
                    navigate(`${redirectPath}?openDatasource=true&connector=${encodeURIComponent(targetConnectorType)}`);
                } else {
                    navigate(redirectPath);
                }
                return;
            }

            if (parsedMessage.command === "outlookTokenResult") {
                console.log("Received outlook token result:", parsedMessage);
                const payload = parsedMessage.data || parsedMessage;
                handleDatasourceTokenResult('outlook', payload);
            } else if (parsedMessage.command === "outlookActionTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging Outlook action token:", error);
                }
                const targetCaseId = caseIdRef.current || payload.caseId || '';
                const targetExportId = exportIdRef.current;
                const redirectPath = targetCaseId ? `/case/${targetCaseId}` : '/case';
                
                console.log("outlookActionTokenResult - flowType:", flowTypeRef.current, "exportId:", targetExportId);
                
                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    console.log("Navigating to returnUrl:", returnUrl);
                    navigate(returnUrl);
                    return;
                }

                // Add query parameters to reopen the Actions modal and specific action
                if (flowTypeRef.current === 'action' && targetExportId) {
                    console.log("Navigating to Actions modal with exportId:", targetExportId);
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                } else if (flowTypeRef.current === 'datasource') {
                    console.log("Navigating to Datasource modal (fallback)");
                    navigate(`${redirectPath}?openDatasource=true&connector=OutlookAPI`);
                } else {
                    console.log("Navigating without query params");
                    navigate(redirectPath);
                }
            } else if (parsedMessage.command === "gmailActionTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging Gmail action token:", error);
                }
                const targetCaseId = caseIdRef.current || payload.caseId || '';
                const targetExportId = exportIdRef.current;
                const redirectPath = targetCaseId ? `/case/${targetCaseId}` : '/case';

                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    navigate(returnUrl);
                    return;
                }

                if (flowTypeRef.current === 'gmailAction' && targetExportId) {
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                } else {
                    navigate(redirectPath);
                }
            } else if (parsedMessage.command === "teamsActionTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging Teams action token:", error);
                }
                const targetCaseId = caseIdRef.current || payload.caseId || '';
                const targetExportId = exportIdRef.current;
                const redirectPath = targetCaseId ? `/case/${targetCaseId}` : '/case';

                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    navigate(returnUrl);
                    return;
                }

                if (flowTypeRef.current === 'teamsAction' && targetExportId) {
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                } else {
                    navigate(redirectPath);
                }
            } else if (parsedMessage.command === "linkedinActionTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging LinkedIn action token:", error);
                }
                const targetCaseId = caseIdRef.current || payload.caseId || '';
                const targetExportId = exportIdRef.current;
                const redirectPath = targetCaseId ? `/case/${targetCaseId}` : '/case';

                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    navigate(returnUrl);
                    return;
                }

                if (flowTypeRef.current === 'linkedinAction' && targetExportId) {
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                } else {
                    navigate(redirectPath);
                }
            } else if (parsedMessage.command === "gmailTokenResult") {
                console.log("Received gmail token result:", parsedMessage);
                const payload = parsedMessage.data || parsedMessage;
                handleDatasourceTokenResult('gmail', payload);
            } else if (parsedMessage.command === "oneDriveTokenResult") {
                console.log("Received onedrive token result:", parsedMessage);
                const payload = parsedMessage.data || parsedMessage;
                handleDatasourceTokenResult('onedrive', payload);
            } else if (parsedMessage.command === "googleDriveTokenResult") {
                console.log("Received googledrive token result:", parsedMessage);
                const payload = parsedMessage.data || parsedMessage;
                handleDatasourceTokenResult('googledrive', payload);
            }
        });

        // Cleanup on unmount
        return () => {
            unsubscribe();
        };
    }, [subscribeToMessages, navigate]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ textAlign: "center", marginTop: "2rem" }}>
                {/* we let case dashboard to notify the user about the result and next steps */}
                {result === null || result === true && <p>{t("outlook_api_callback.result_processing")}</p>}
                {result === false && (
                    <>
                        <p>{t("outlook_api_callback.result_error")}</p>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(`/case/${caseId}`)}
                        >
                            {t("outlook_api_callback.try_again")}
                        </Button>
                    </>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default CallbackOutlookApiPage;

function decodeOutlookState(stateValue) {
    try {
        const parts = stateValue.split(".");
        const encodedPayload = parts.length >= 2 ? parts[1] : stateValue;
        const normalized = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
        const decoded = window.atob(padded);
        return JSON.parse(decoded);
    } catch (err) {
        console.error("Failed to decode Outlook state payload", err);
        return null;
    }
}

function sanitizeReturnUrl(value) {
    if (!value || typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    if (trimmed.startsWith("/")) {
        return trimmed;
    }

    try {
        const parsed = new URL(trimmed);
        if (parsed.origin !== window.location.origin) {
            return null;
        }
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (err) {
        return null;
    }
}

function ensureDatasourceModal(returnUrl, connector) {
    try {
        const url = new URL(returnUrl, window.location.origin);
        url.searchParams.set("openDatasource", "true");
        url.searchParams.set("connector", connector || "OutlookAPI");
        return `${url.pathname}${url.search}${url.hash}`;
    } catch (err) {
        return returnUrl;
    }
}

function isExchangeCodeUsed(code) {
    if (!code || typeof window === "undefined") {
        return false;
    }
    try {
        const key = `oauth_code_used_${code}`;
        const raw = window.localStorage.getItem(key);
        if (!raw) {
            return false;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.ts) {
            return true;
        }
        if (Date.now() - parsed.ts > 5 * 60 * 1000) {
            window.localStorage.removeItem(key);
            return false;
        }
        return true;
    } catch (err) {
        return false;
    }
}

function markExchangeCodeUsed(code) {
    if (!code || typeof window === "undefined") {
        return;
    }
    try {
        const key = `oauth_code_used_${code}`;
        window.localStorage.setItem(key, JSON.stringify({ ts: Date.now() }));
    } catch (err) {
        // ignore storage errors
    }
}

function handleDuplicateExchangeRedirect(
    navigate,
    flowTypeRef,
    returnUrlRef,
    caseIdRef,
    exportIdRef,
    datasourceIdRef,
    connectorTypeRef
) {
    const returnUrl = returnUrlRef.current;
    if (returnUrl) {
        if (flowTypeRef.current === 'datasource') {
            navigate(ensureDatasourceModal(returnUrl, "OutlookAPI"));
            return;
        }
        if (flowTypeRef.current === 'gmail') {
            navigate(ensureDatasourceModal(returnUrl, "Gmail"));
            return;
        }
        if (flowTypeRef.current === 'onedrive') {
            navigate(ensureDatasourceModal(returnUrl, "OneDrive"));
            return;
        }
        if (flowTypeRef.current === 'googledrive') {
            navigate(ensureDatasourceModal(returnUrl, "GoogleDrive"));
            return;
        }
        if (flowTypeRef.current === 'pluginConnector') {
            const connector = connectorTypeRef?.current || "OutlookAPI";
            navigate(ensureDatasourceModal(returnUrl, connector));
            return;
        }
        navigate(returnUrl);
        return;
    }
    const targetCaseId = caseIdRef.current || '';
    const redirectPath = targetCaseId ? `/case/${targetCaseId}` : '/case';
    if ((flowTypeRef.current === 'action' || flowTypeRef.current === 'pluginAction' || flowTypeRef.current === 'gmailAction' || flowTypeRef.current === 'teamsAction' || flowTypeRef.current === 'linkedinAction') && exportIdRef.current) {
        navigate(`${redirectPath}?openActions=true&exportId=${exportIdRef.current}`);
        return;
    }
    if (flowTypeRef.current === 'datasource') {
        navigate(`${redirectPath}?openDatasource=true&connector=OutlookAPI`);
        return;
    }
    if (flowTypeRef.current === 'onedrive') {
        navigate(`${redirectPath}?openDatasource=true&connector=OneDrive`);
        return;
    }
    if (flowTypeRef.current === 'googledrive') {
        navigate(`${redirectPath}?openDatasource=true&connector=GoogleDrive`);
        return;
    }
    if (flowTypeRef.current === 'pluginConnector') {
        const connector = connectorTypeRef?.current || "OutlookAPI";
        if (datasourceIdRef?.current) {
            navigate(`${redirectPath}?openDatasource=true&connector=${encodeURIComponent(connector)}&datasourceId=${encodeURIComponent(datasourceIdRef.current)}`);
            return;
        }
        navigate(`${redirectPath}?openDatasource=true&connector=${encodeURIComponent(connector)}`);
        return;
    }
    navigate(redirectPath);
}
